import type { MessengerWebSocketEvent } from '@widgets/messenger/types/api/websocket'
import {
  type MessengerChatRatedLogMessageListItem,
  type MessengerCommentLeftLogMessageListItem,
  type MessengerIncomingMessageListItem,
  type MessengerInitialMessageListItem,
  type MessengerMessageListItem,
  type MessengerMessageListItemBase,
  type MessengerMessageLogListItem,
  MessengerMessageStatus,
  type MessengerSentMessageListItem,
  type MessengerUserJoinedLogMessageListItem,
  type MessengerUserLeftLogMessageListItem,
} from '@widgets/messenger/types/chat/message'
import {
  isRestApiChatRatedLogMessage,
  isRestApiCommentLeftLogMessage,
  isRestApiIncomingTextMessage,
  isRestApiInitialMessage,
  isRestApiLogMessage,
  isRestApiSentTextMessage,
  isRestApiUserJoinedLogMessage,
  isRestApiUserLeftLogMessage,
  type MessengerRestApiIncomingTextMessage,
  type MessengerRestApiInitialMessage,
  type MessengerRestApiLogMessage,
  type MessengerRestApiMessage,
  type MessengerRestApiSentTextMessage,
} from '@widgets/messenger/types/api/rest'
import type { MessengerApiAttachment } from '@widgets/messenger/types/api/common'
import type { WidgetUploadedAttachment } from '@widgets/shared/types'
import { isImageMime } from '@/utils/file/mime'

export class MessengerMessageListItemCastService {
  // Common

  public castApiAttachment(attachment: MessengerApiAttachment): WidgetUploadedAttachment {
    let previewUrl = attachment.previewUrl || undefined
    if (!previewUrl && isImageMime(attachment.mimeType)) {
      previewUrl = attachment.url
    }
    return {
      type: 'uploadedAttachment',
      id: attachment.id,
      name: attachment.originalName,
      size: attachment.size,
      mimeType: attachment.mimeType,
      downloadUrl: attachment.url,
      previewUrl,
      metadata: attachment.metadata || undefined,
    }
  }

  public castApiAttachments(attachments: MessengerApiAttachment[]): WidgetUploadedAttachment[] {
    return attachments.map((attachment) => this.castApiAttachment(attachment))
  }

  // From WebSocket

  public castWebSocketSentMessageEvent(event: MessengerWebSocketEvent<'messageSent'>): MessengerSentMessageListItem {
    return {
      type: 'sent',
      id: event.payload.id,
      messageTime: event.payload.messageTime,
      text: event.payload.text,
      attachments: event.payload.files ? this.castApiAttachments(event.payload.files) : [],
      status: event.payload.status,
      trackId: event.payload.trackId || '',
    }
  }

  public castWebSocketIncomingMessageEvent(
    event: MessengerWebSocketEvent<'messageIncoming'>,
  ): MessengerIncomingMessageListItem {
    return {
      type: 'incoming',
      id: event.payload.id,
      messageTime: event.payload.messageTime,
      text: event.payload.text,
      attachments: event.payload.files ? this.castApiAttachments(event.payload.files) : [],
      firstName: event.payload.firstName || '',
      lastName: event.payload.lastName || '',
    }
  }

  public castWebSocketInitialMessageEvent(
    event: MessengerWebSocketEvent<'messageInitial'>,
  ): MessengerInitialMessageListItem {
    return {
      type: 'initial',
      id: event.payload.id,
      messageTime: event.payload.messageTime,
      name: event.payload.name,
      email: event.payload.email,
      text: event.payload.text,
    }
  }

  public castWebSocketLogMessageEvent(
    event: MessengerWebSocketEvent<'messageLog'>,
  ): MessengerMessageLogListItem | undefined {
    const baseBody = {
      type: 'log',
      id: event.payload.id,
      messageTime: event.payload.messageTime,
    } satisfies MessengerMessageListItemBase<'log'>

    if (event.payload.type === 'userJoined') {
      return {
        ...baseBody,
        logType: 'userJoined',
        fullName: event.payload.data.userFullName,
      } satisfies MessengerUserJoinedLogMessageListItem
    }
    if (event.payload.type === 'userLeft') {
      return {
        ...baseBody,
        logType: 'userLeft',
        fullName: event.payload.data.userFullName,
        all: event.payload.data.all,
      } satisfies MessengerUserLeftLogMessageListItem
    }
    if (event.payload.type === 'chatRated') {
      return {
        ...baseBody,
        logType: 'chatRated',
        rate: event.payload.data.rate,
      } satisfies MessengerChatRatedLogMessageListItem
    }
    if (event.payload.type === 'commentLeft') {
      return {
        ...baseBody,
        logType: 'commentLeft',
        text: event.payload.data.content,
      } satisfies MessengerCommentLeftLogMessageListItem
    }

    return undefined
  }

  // From REST API

  public castRestApiMessages(messages: MessengerRestApiMessage[]): MessengerMessageListItem[] {
    const listItems: MessengerMessageListItem[] = []
    for (const message of messages) {
      const listItem = this.castRestApiMessage(message)
      if (listItem) {
        listItems.push(listItem)
      }
    }
    return listItems
  }

  public castRestApiMessage(message: MessengerRestApiMessage): MessengerMessageListItem | undefined {
    if (isRestApiSentTextMessage(message)) {
      return this.castRestApiSentTextMessage(message)
    }
    if (isRestApiIncomingTextMessage(message)) {
      return this.castRestApiIncomingTextMessage(message)
    }
    if (isRestApiInitialMessage(message)) {
      return this.castRestApiInitialMessage(message)
    }
    if (isRestApiLogMessage(message)) {
      return this.castRestApiLogMessage(message)
    }
    return undefined
  }

  public castRestApiSentTextMessage(message: MessengerRestApiSentTextMessage): MessengerSentMessageListItem {
    return {
      type: 'sent',
      id: message.id,
      messageTime: message.createdAt,
      text: message.payload.content,
      attachments: this.castApiAttachments(message.files),
      status: message.status === 'delivered' ? MessengerMessageStatus.DELIVERED : MessengerMessageStatus.QUEUE,
      trackId: '',
    }
  }

  public castRestApiIncomingTextMessage(
    message: MessengerRestApiIncomingTextMessage,
  ): MessengerIncomingMessageListItem {
    return {
      type: 'incoming',
      id: message.id,
      messageTime: message.createdAt,
      text: message.payload.content,
      attachments: this.castApiAttachments(message.files),
      firstName: message.user.firstName,
      lastName: message.user.lastName,
    }
  }

  public castRestApiInitialMessage(message: MessengerRestApiInitialMessage): MessengerInitialMessageListItem {
    return {
      type: 'initial',
      id: message.id,
      messageTime: message.createdAt,
      name: message.payload.name,
      email: message.payload.email,
      text: message.payload.text,
    }
  }

  public castRestApiLogMessage(message: MessengerRestApiLogMessage): MessengerMessageLogListItem | undefined {
    const baseBody = {
      type: 'log',
      id: message.id,
      messageTime: message.createdAt,
    } satisfies MessengerMessageListItemBase<'log'>

    if (isRestApiUserJoinedLogMessage(message)) {
      return {
        ...baseBody,
        logType: 'userJoined',
        fullName: [message.user.firstName, message.user.lastName].filter(Boolean).join(' '),
      } satisfies MessengerUserJoinedLogMessageListItem
    }
    if (isRestApiUserLeftLogMessage(message)) {
      return {
        ...baseBody,
        logType: 'userLeft',
        fullName: [message.user.firstName, message.user.lastName].filter(Boolean).join(' '),
        all: message.payload.payload.all,
      } satisfies MessengerUserLeftLogMessageListItem
    }
    if (isRestApiChatRatedLogMessage(message)) {
      return {
        ...baseBody,
        logType: 'chatRated',
        rate: message.payload.payload.payload.rate,
      } satisfies MessengerChatRatedLogMessageListItem
    }
    if (isRestApiCommentLeftLogMessage(message)) {
      return {
        ...baseBody,
        logType: 'commentLeft',
        text: message.payload.payload.payload.content,
      } satisfies MessengerCommentLeftLogMessageListItem
    }
    return undefined
  }
}

export default new MessengerMessageListItemCastService()
